import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { catchError, filter, interval, of, startWith, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { ConversationApiService } from '@vendasta/conversation';
import { LEAD_CAPTURE_TAG } from './analytics.constants';
import { isAnonymous, isLeadCaptured } from '../../util/visitor';

const POLLING_INTERVAL = 3000;

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService implements OnDestroy {
  private readonly destroy$$ = new Subject<void>();
  private readonly conversationApiService = inject(ConversationApiService);
  private readonly isPolling = signal(false);
  private namespace: string;
  private conversationId: string;
  private parentOrigin = '*';

  pushEvent(eventName: string): void {
    const event: Record<string, string> = { event: eventName };
    if (this.namespace) {
      event['namespace'] = this.namespace;
    }
    if (this.conversationId) {
      event['conversationId'] = this.conversationId;
    }

    window.parent.postMessage({ type: 'webchat:analytics', detail: event }, this.parentOrigin);
  }

  startPollingForLeadCapturedEvent(): void {
    if (this.isPolling()) {
      return;
    }

    interval(POLLING_INTERVAL)
      .pipe(
        startWith(0),
        switchMap(() => this.conversationApiService.getWidgetConversation().pipe(catchError(() => of(null)))),
        tap((data) => {
          if (isAnonymous(data) && !this.isPolling()) {
            this.isPolling.set(true);
          }
        }),
        filter((_) => this.isPolling()),
        tap((data) => {
          if (isLeadCaptured(data)) {
            this.pushEvent(LEAD_CAPTURE_TAG);
            this.stopPollingForLeadCapturedEvent();
          }
        }),
        takeUntil(this.destroy$$),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.stopPollingForLeadCapturedEvent();
    this.destroy$$.complete();
  }

  setNamespace(namespace: string): void {
    this.namespace = namespace;
  }

  setConversationId(conversationId: string): void {
    this.conversationId = conversationId;
  }

  setParentOrigin(origin: string): void {
    this.parentOrigin = origin;
  }

  private stopPollingForLeadCapturedEvent(): void {
    this.isPolling.set(false);
    this.destroy$$.next();
  }
}
